/* eslint-disable react/prop-types */
import {
	Create,
	List,
	Datagrid,
	Edit,
	SimpleForm,
	TextField,
	TextInput,
	ChipField,
	ReferenceField,
	Labeled,
	useNotify,
	useRefresh,
	useUnselectAll,
	Button,
	useDataProvider,
	useListContext,
	useRecordContext,
	Toolbar,
	SaveButton,
	DeleteButton
} from 'react-admin';
import { useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box } from '@mui/material';

import { enforceGroupId, formatFileSize } from '../../util';
import { StatusChip } from './components';

export const StatusField = () => {
	const {status} = useRecordContext();
	return <StatusChip status={status} />;
};

export const FileSizeField = ({source}) => {
	const record = useRecordContext();
	const value = record?.[source] ? formatFileSize( record[source] ) : '';
	return <span>{value}</span>;

}

// Re-extract button for single document in edit form
const ReExtractButton = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const dataProvider = useDataProvider();

	const handleReExtract = async () => {
		if (!record?.id) return;

		try {
			const { data } = await dataProvider.refreshDocumentsContent({ ids: [record.id] });

			if (data.success && data.results.length > 0) {
				const result = data.results[0];
				if (result.success) {
					notify('Content extracted successfully', { type: 'success' });
				} else {
					notify(`Extraction failed: ${result.message}`, { type: 'error' });
				}
				refresh();
			} else {
				notify('Failed to extract content', { type: 'error' });
			}
		} catch (error) {
			notify(`Error: ${error.message}`, { type: 'error' });
		}
	};

	return (
		<Button
			label="Re-extract Content"
			onClick={handleReExtract}
			variant="outlined"
			sx={{ marginLeft: 2 }}
		>
			<RefreshIcon />
		</Button>
	);
};

// Custom toolbar with re-extract button
const DocumentEditToolbar = () => (
	<Toolbar>
		<SaveButton />
		<ReExtractButton />
		<Box sx={{ flex: 1 }} />
		<DeleteButton />
	</Toolbar>
);

const FormFields = (
	<SimpleForm>
		<TextInput source="title" />
		<TextInput source="src" />
		<Labeled label="Topic">
			<ReferenceField source="topic_id" reference="topics">
				<ChipField source="name" />
			</ReferenceField>
		</Labeled>
		<Labeled label="Status">
			<StatusField />
		</Labeled>

	</SimpleForm>
);

const create = () => (
	<Create record={enforceGroupId()}>
		{FormFields}
	</Create>
);

const edit = () => (
	<Edit record={enforceGroupId()}>
		<SimpleForm toolbar={<DocumentEditToolbar />}>
			<TextInput source="title" />
			<TextInput source="src" />
			<Labeled label="Topic">
				<ReferenceField source="topic_id" reference="topics">
					<ChipField source="name" />
				</ReferenceField>
			</Labeled>
			<Labeled label="Status">
				<StatusField />
			</Labeled>
		</SimpleForm>
	</Edit>
);

// Bulk action for refreshing content
const RefreshContentButton = () => {
	const { selectedIds } = useListContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const unselectAll = useUnselectAll('documents');
	const dataProvider = useDataProvider();

	const handleRefresh = async () => {
		try {
			const { data } = await dataProvider.refreshDocumentsContent( { ids: selectedIds } );

			if (data.success) {
				const successCount = data.results.filter(r => r.success).length;
				const failCount = data.results.length - successCount;

				let message = `${successCount} document(s) processed successfully`;
				if (failCount > 0) {
					message += `, ${failCount} failed`;
				}

				notify(message, { type: successCount > 0 ? 'success' : 'warning' });
				refresh();
				unselectAll();

			} else {
				notify('Failed to refresh content', { type: 'error' });
			}
			
		} catch (error) {
			notify(`Error: ${error.message}`, { type: 'error' });
		}
	};

	return (
		<Button
			label="Refresh Content"
			onClick={handleRefresh}
		>
			<RefreshIcon />
		</Button>
	);
};

const ReindexContentButton = () => {
	const { selectedIds } = useListContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const unselectAll = useUnselectAll('documents');
	const dataProvider = useDataProvider();

	const handleRefresh = async () => {


	};

	return (
		<Button label="Reindex Content" onClick={handleRefresh}>
			<RefreshIcon />
			
		</Button>
	);
}

const DocumentBulkActionButtons = () => (
	<>
		<RefreshContentButton />
		<ReindexContentButton />
	</>
);

// Auto-polling component for documents that are indexing
const DocumentListWithPolling = () => {
	const { data } = useListContext();
	const refresh = useRefresh();

	useEffect(() => {
		// Check if any documents are currently indexing or reindexing
		const hasIndexing = data?.some(doc =>
			doc.status === 'indexing' || doc.status === 'reindexing'
		);

		if (hasIndexing) {
			// Poll every X seconds while indexing
			const interval = setInterval(() => {
				refresh();
			}, 10000);

			return () => clearInterval(interval);
		}
	}, [data, refresh]);

	return (
		<Datagrid bulkActionButtons={<DocumentBulkActionButtons />}>
			<TextField source="title" />
			<ReferenceField source="topic_id" reference="topics">
				<ChipField source="name" />
			</ReferenceField>
			<StatusField />
			<FileSizeField source="content_length" />
			<FileSizeField source="byte_length" label="File Size" />
		</Datagrid>
	);
};

const list = () => (
	<List>
		<DocumentListWithPolling />
	</List>
)

export const Document = {
	list,
	edit,
	create
};

export default Document;